import React, { useEffect, useState } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import {
  BookOpen,
  Settings,
  FileText,
  ChevronDown,
  ChevronRight,
  X,
  LogOut,
} from "lucide-react";
import { Avatar, AvatarFallback, iconMap } from "./Sidebar"
import sidebarData from "../../../../Assets/constants/sidebar_new.json";
import Feedback from "../../../Dashboard/Sidebar/Modals/feedback";
import ShareFeedback from "../../../Dashboard/Sidebar/Modals/share-feedback";
import PID_MAP from "../../../../Assets/constants/inclusion_pid.json";


// Icon mapping
// const iconMap = {
//   Home,
//   BookOpen,
//   Calendar,
//   Briefcase,
//   Settings,
//   HelpCircle,
//   Star,
//   Trophy,
//   FileText,
//   MessageCircle,
//   Users,
//   BarChart3,
//   GraduationCap,
//     BanknoteArrowDown,
//   ReceiptIndianRupee,
//   FileCheck,
//   FileBadge,
//   Newspaper,
//     NotepadTextDashed,
//   BriefcaseBusiness,
//   BookMarked
// };

// Custom Badge Component
const Badge = ({ children, className = "" }) => {
  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-600 text-white ${className}`}
    >
      {children}
    </span>
  );
};

const MobileSidebar = ({ isOpen, onClose, showInclusionModal, hasActivePlan }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeItem, setActiveItem] = useState("");
  const [expandedSections, setExpandedSections] = useState({});
  const [filteredSidebarData, setFilteredSidebarData] = useState([]);
  const [isModalOpen, setIsOpen] = useState("");
  const inclusion_link = localStorage.getItem("inclusion_link");
  const program_inclusion_pid = localStorage.getItem("program_id");
  const program_inclusive_id = PID_MAP[program_inclusion_pid];
  // Mock user data
  const user = {
    name:
      localStorage.getItem("username") + " " + localStorage.getItem("lastname"),
    email: localStorage.getItem("email"),
    avatar: localStorage.getItem("profileURL"),
  };

  // useEffect(() => {
  //   // Get category_id from localStorage

  //   const userCategoryId = localStorage.getItem("category");

  //   // If no category_id exists, show all items

  //   if (!userCategoryId) {
  //     setFilteredSidebarData(sidebarData);

  //     return;
  //   }

  //   // Filter sidebar items based on category access

  //   const filtered = sidebarData.filter((item) => {
  //     // If categories array is empty, show to all users

  //     if (!item.categories || item.categories.length === 0) {
  //       return true;
  //     }

  //     // Check if user's category_id is in the item's categories array

  //     return item.categories.includes(userCategoryId);
  //   });

  //   setFilteredSidebarData(filtered);
  // }, []);

  useEffect(() => {
    const userCategoryId = localStorage.getItem("category");
    const userStatus = localStorage.getItem("status")?.toLowerCase();
    const term = Number(localStorage.getItem("term"));
    console.log("User Category ID:", userCategoryId);
    console.log("User Status:", userStatus);

    if (!userCategoryId) {
      setFilteredSidebarData(sidebarData);
      return;
    }

    // Helper function to check if an item should be visible
    const isItemVisible = (item, categoryId) => {
      // If no categories array or empty, show to all users
      if (!item.categories || item.categories.length === 0) {
        console.log(`${item.name}: No categories - visible to all`);
        return true;
      }
      // Check if user's category is in the item's categories
      const isVisible = item.categories.includes(categoryId);
      console.log(
        `${item.name}: ${isVisible ? "✓" : "✗"} (has ${item.categories.length
        } categories)`
      );
      return isVisible;
    };

    // For active users
    if (userStatus === "active") {
      const filtered = sidebarData
        .filter((item) => isItemVisible(item, userCategoryId))
        .map((item) => {
          // If item has children, filter them based on their categories
          if (item.children && item.children.length > 0) {
            let filteredChildren = item.children.filter((child) =>
              isItemVisible(child, userCategoryId)
            );

            // Hide Installment Tracker if no active plan
            if (!hasActivePlan) {
              filteredChildren = filteredChildren.filter(
                (child) => child.id !== "installment-tracker"
              );
            }

            if (item.id === "career" && term < 3) {
              filteredChildren = filteredChildren.filter(
                (child) => child.name !== "Career Counselling"
              );
            }

            // Only return parent if it has visible children
            if (filteredChildren.length > 0) {
              return {
                ...item,
                children: filteredChildren,
              };
            }
            // If no children visible, don't show the parent dropdown
            return null;
          }
          return item;
        })
        .filter(Boolean);

      console.log("Filtered sidebar for active user:", filtered);
      setFilteredSidebarData(filtered);
    } else {
      // For inactive users - show only specific features
      const allowedFeaturesForInactive = [
        "home",
        "feedback",
        "dashboard",
        "share-feedback",
        "manage-my-program",
        "fee-recipt",
        "due-fee-payment",
        "my-applications",
        "program-inclusion",
        // Only show installment tracker if there is an active plan
        ...(hasActivePlan ? ["installment-tracker"] : []),
      ];

      const filtered = sidebarData
        .filter((item) => {
          // Check if parent is allowed
          if (!allowedFeaturesForInactive.includes(item.id)) {
            return false;
          }

          if (["dashboard", "my-applications"].includes(item.id)) {
            return true;
          }

          // Check category visibility
          return isItemVisible(item, userCategoryId);
        })
        .map((item) => {
          // If item has children, filter by both allowed IDs and categories
          if (item.children && item.children.length > 0) {
            const filteredChildren = item.children.filter((child) => {
              // Check if child is in allowed list
              const isAllowed = allowedFeaturesForInactive.includes(child.id);
              // Check category visibility
              const hasAccess = isItemVisible(child, userCategoryId);
              return isAllowed && hasAccess;
            });

            if (filteredChildren.length > 0) {
              return {
                ...item,
                children: filteredChildren,
              };
            }
            return null;
          }
          return item;
        })
        .filter(Boolean);

      console.log("Filtered sidebar for inactive user:", filtered);
      setFilteredSidebarData(filtered);
    }
  }, [hasActivePlan]);

  useEffect(() => {
    if (filteredSidebarData.length > 0 && !activeItem) {
      setActiveItem(filteredSidebarData[0].id);
    }
  }, [filteredSidebarData, activeItem]);

  useEffect(() => {
    // Check for tab query parameter first
    const tabParam = searchParams.get("tab");
    if (tabParam) {
      setActiveItem(tabParam);
      return;
    }

    // Fallback to pathname matching
    const currentPath = location.pathname;

    for (const item of filteredSidebarData) {
      if (item.link === currentPath) {
        setActiveItem(item.id);
        return;
      }

      if (item.children) {
        for (const child of item.children) {
          if (child.link === currentPath) {
            // Set child ID as active with parent ID prefix
            const childId =
              child.id ||
              `${item.id}-${child.name.toLowerCase().replace(/\s+/g, "-")}`;
            setActiveItem(childId);
            // Expand parent section
            setExpandedSections((prev) => ({
              ...prev,
              [item.name]: true,
            }));
            return;
          }
        }
      }
    }

    // Set default if nothing matches
    // if (sidebarData.length > 0 && !activeItem) {
    //   setActiveItem(sidebarData[0].id);
    if (filteredSidebarData.length > 0 && !activeItem) {
      setActiveItem(filteredSidebarData[0].id);
    }
  }, [searchParams, location.pathname, filteredSidebarData, activeItem]);

  const toggleSection = (itemName) => {
    setExpandedSections((prev) => ({
      ...prev,
      [itemName]: !prev[itemName],
    }));
  };

  const handleLogout = () => {
    console.log("Logging out...");
  };

  const LogOutUser = (name, domain) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; domain=${domain}; path=/;`;
    localStorage.clear();
    window.location.href = "https://accredian.com";
  };

  const handleItemClick = (item, parentId = null) => {
    // Generate unique ID for child items
    if (item.type === "modal") {
      setIsOpen(item.id); // This will set "shareFeedback" as the value
      return;
    }

    if (item.type === "external" && item.id == "events-calendar") {
      const newemail = btoa(localStorage.getItem("email"));
      window.open(item.link + newemail, "_blank");
      return;
    }

    if (item.type === "external") {
      window.open(item.link, "_blank");
      return;
    }

    if (item.type === "inclusion") {
      if (inclusion_link) {
        window.open(inclusion_link, "_blank");
      } else {
        window.open(item.link + "/" + program_inclusive_id, "_blank");
      }
      return;
    }
    const itemId = parentId
      ? item.id || `${parentId}-${item.name.toLowerCase().replace(/\s+/g, "-")}`
      : item.id;

    setActiveItem(itemId);

    // Close mobile sidebar after navigation
    if (onClose) {
      onClose();
    }
  };

  const handleTabClick = (tabName) => {
    navigate(`/?tab=${tabName}`);
    setActiveItem(tabName);
    if (onClose) {
      onClose();
    }
  };

  const renderNavItem = (item, isChild = false, parentId = null) => {
    const IconComponent = iconMap[item.icon] || Settings;

    // Generate unique ID for child items
    const itemId =
      isChild && parentId
        ? item.id ||
        `${parentId}-${item.name.toLowerCase().replace(/\s+/g, "-")}`
        : item.id;

    const isActive = activeItem === itemId;

    const navItemContent = (
      <div
        className={`flex items-center gap-3 p-3 rounded-lg transition-all duration-200 cursor-pointer group ${isChild ? "ml-6" : ""
          } ${isActive
            ? "bg-blue-600 text-white shadow-sm"
            : "hover:bg-gray-100 hover:text-gray-900 text-gray-600"
          }`}
        onClick={(e) => {
          if (item.children && !isChild) {
            toggleSection(item.name);
          } else {
            handleItemClick(item, parentId);
          }
        }}
      >
        <IconComponent
          className={`w-5 h-5 flex-shrink-0 ${isActive ? "text-white" : "text-gray-500 group-hover:text-gray-900"
            }`}
        />

        <span
          className={`text-sm font-medium flex-1 ${isActive ? "text-white" : "text-gray-600 group-hover:text-gray-900"
            }`}
        >
          {item.name}
        </span>

        <div className="flex items-center gap-2">
          {item.badge && (
            <Badge className="text-xs px-2 rounded-full py-1 bg-red-500 text-white">
              {item.badge}
            </Badge>
          )}
          {item.isNew && (
            <Badge className="text-xs px-2 py-1 bg-blue-600 text-white">
              New
            </Badge>
          )}
          {item.children &&
            (expandedSections[item.name] ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            ))}
        </div>
      </div>
    );

    // Only add Link wrapper if item has a link
    const finalContent =
      item.link &&
        item.type !== "modal" &&
        item.type !== "external" &&
        item.type !== "inclusion" ? (
        item.id === "dashboard" ? (
          <a
            href="/dashboard"
            style={{ textDecoration: "none", color: "inherit" }}
          >
            {navItemContent}
          </a>
        ) : (
          <Link
            to={item.link}
            style={{ textDecoration: "none", color: "inherit" }}
          >
            {navItemContent}
          </Link>
        )
      ) : (
        navItemContent
      );

    return (
      <div key={item.name}>
        {finalContent}

        {/* Nested children */}
        {item.children &&
          expandedSections[item.name] &&
          item.children.map((child) => (
            <div key={child.name}>
              {child.link && child.type !== "inclusion" ? (
                <Link
                  to={child.link}
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  {renderNavItem(child, true, item.id)}
                </Link>
              ) : (
                renderNavItem(child, true, item.id)
              )}
            </div>
          ))}
      </div>
    );
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[999] md:hidden transition-opacity duration-300"
          onClick={onClose}
        />
      )}

      {/* Mobile Sidebar */}
      <div
        className={`fixed top-0 left-0 h-screen w-80 max-w-[85vw] bg-white border-r border-gray-200 shadow-lg z-[9999] 
                   transform transition-transform duration-300 ease-in-out md:hidden flex flex-col ${isOpen ? "translate-x-0" : "-translate-x-full"
          }`}
      >
        {/* Header with Close Button */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <img
            onClick={() => {
              window.open("https://accredian.com", "_blank");
            }}
            className="h-10 cursor-pointer"
            src="https://storage.googleapis.com/accredian-assets/Frontend_Assests/Images/Accredian-react-site-images/other/logo.webp"
            alt="Accredian"
          />
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Navigation - Scrollable */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {location.pathname === "/studentDetails" ? (
            // Render only 2 tabs for home page
            <>
              <button
                className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 bg-blue-600 text-white`}
              >
                <div className="flex items-center gap-3">
                  <BookOpen size={20} />
                  <span className="font-medium">Student Details</span>
                </div>
              </button>
            </>
          ) : showInclusionModal ? (
            // Render specific sidebar items when inclusion modal is active
            <>
              {filteredSidebarData
                .filter((item) => ["dashboard", "my-applications"].includes(item.id))
                .map((item) => renderNavItem(item))}

              {/* <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-100 flex flex-col items-center text-center space-y-2"> */}
              {/* <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                  <FileText className="w-4 h-4" />
                </div> */}
              {/* <p className="text-xs font-medium text-blue-800">
                  Complete program inclusion verification to unlock all features.
                </p> */}
              {/* </div> */}
            </>
          ) : (
            // Render normal sidebar items
            <>
              {filteredSidebarData.map((item) => renderNavItem(item))}
              <Feedback open={isModalOpen} setOpen={setIsOpen} />
              <ShareFeedback open={isModalOpen} setOpen={setIsOpen} />
            </>
          )}
        </nav>

        {/* User Profile Section */}
        <div className="border-t border-gray-200 p-4 bg-white">
          <div className="space-y-3">
            {/* User Info */}
            <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
              <Avatar className="w-10 h-10 ring-2 ring-gray-200">
                {user.avatar ? (
                  <img
                    src={user.avatar}
                    alt={user.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <AvatarFallback className="bg-gray-100 text-gray-600">
                    {(() => {
                      const nameParts = user.name.trim().split(" ");
                      const firstInitial = nameParts[0]?.charAt(0) || "";
                      const lastInitial =
                        localStorage
                          .getItem("lastname")
                          ?.split(" ")[0]
                          ?.charAt(0) || "";
                      console.log(
                        localStorage.getItem("lastname")?.split(" ")[0]
                      );
                      return (firstInitial + lastInitial).toUpperCase();
                    })()}
                  </AvatarFallback>
                )}
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user.name}
                </p>
                <p className="text-xs text-gray-500 truncate">{user.email}</p>
              </div>
            </div>

            {/* Logout Button */}
            <button
              onClick={() => {
                LogOutUser("token", "accredian.com");
              }}
              className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-red-50 hover:text-red-600 transition-colors group"
            >
              <LogOut className="w-4 h-4" />
              <span className="text-sm font-medium">Logout</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default MobileSidebar;
